import React, { SyntheticEvent, useEffect, useRef, useState } from "react";
import Parser from "html-react-parser";
import { v4 as uuid } from "uuid";
import { breakpoints, useMediaQuery } from "@tvg/design-system";
import { Paragraph, Button } from "@tvg/design-system/web";
import tvgConf from "@tvg/conf";
import { TvgConf } from "@tvg/conf/src/types";
import {
  PromosLegalsWrapper,
  PromosLegalsContainer,
  ButtonWrapper
} from "./styled-components";
import {
  RenderRichTextInlineComp,
  RenderRichTextParagraph
} from "../richTextEditorResolver";
import { RichText, RichTextInlineComp } from "../../types";

export interface LegalItemMark {
  type: string;
  attrs?: {
    href?: string;
    uuid?: string | null;
    target?: string | null;
    linktype?: string;
    class?: string;
  };
}

export interface LegalItemContent {
  text?: string;
  type: string;
  marks?: LegalItemMark[];
}

export interface LegalItem {
  type: string;
  content: Array<{
    type: string;
    content: LegalItemContent[];
  }>;
}

export interface Blok {
  _uid: string;
  component: string;
  legalItem?: LegalItem;
  _editable?: string;
}

export interface Props {
  content: {
    bloks: Blok[];
  };
}

const TRANSPARENT_BUTTON_STYLE = {
  color: "var(--fd-colors-content-link)",
  background: "transparent",
  border: "none",
  boxShadow: "none"
};

const PromosLegalsContent = ({ content }: Props) => {
  const isMobile = useMediaQuery(breakpoints.tablet.max.sm);
  const [expanded, setExpanded] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const [showGradient, setShowGradient] = useState(false);

  useEffect(() => {
    if (textRef.current) {
      const element = textRef.current;
      const lineHeight = parseFloat(
        getComputedStyle(element).lineHeight || "20"
      );
      const maxHeight = lineHeight * 5;

      setShowGradient(element.scrollHeight > maxHeight);
    }
  }, []);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const tvgConfInstance: TvgConf = tvgConf();
  const { product } = tvgConfInstance;
  const iosApp: boolean = product.indexOf("ios") > -1;

  const setExternalPath = (url: string, text: string): JSX.Element => {
    const isInternal: boolean = /fdbox|fanduel(\.|-)|racing/gi.test(url);
    const target: string = isInternal ? "_self" : "_blank";

    return (
      <a
        href={url}
        target={target}
        onClick={(event) => {
          redirect(event, url);
        }}
      >
        {text}
      </a>
    );
  };

  const redirect = (event: SyntheticEvent<HTMLAnchorElement>, url: string) => {
    if (iosApp) {
      event.preventDefault();
      event.stopPropagation();
      window.parent.postMessage({ type: "open_url", payload: url }, "*");
    }
  };

  const legalBlock = content?.bloks?.find(
    (blok) => blok.component === "legal_content"
  );
  const legalContent = legalBlock?.legalItem;

  if (!legalBlock || !legalContent) return null;

  let legalText = null;

  const listOfContents: { type: string }[] = [];
  if (typeof legalContent !== "string") {
    legalContent?.content?.forEach((blok) =>
      listOfContents.push(blok as unknown as RichText)
    );
  } else {
    legalText = (
      <div ref={textRef}>
        <Paragraph qaLabel="legal-text" color="--fd-colors-content-subtle">
          <>{Parser(legalContent)}</>
        </Paragraph>
      </div>
    );
  }

  return (
    <PromosLegalsWrapper isMobile={isMobile}>
      <PromosLegalsContainer
        data-qa-label="promosLegalContainer"
        expanded={expanded}
        showGradient={showGradient}
      >
        <Paragraph
          marginBottom="var(--fd-space-space-3)"
          fontSize="xs"
          color="--fd-colors-content-subtle"
          fontFamily="bold"
        >
          Terms & Conditions
        </Paragraph>
        {legalText}
        {listOfContents.map((blok) => {
          if (blok.type === "paragraph") {
            const actualText = RenderRichTextParagraph(blok as RichText);
            return (
              <div ref={textRef} key={uuid()}>
                <Paragraph
                  qaLabel="terms-and-conditions-blok"
                  key={uuid()}
                  color="--fd-colors-content-subtle"
                  fontSize="xs"
                >
                  <>
                    {" "}
                    {Parser(actualText as string, {
                      replace: (domNode) => {
                        //  @ts-ignore
                        if (domNode.name === "a") {
                          //  @ts-ignore
                          if (domNode.attribs.linktype !== "story") {
                            return setExternalPath(
                              //  @ts-ignore
                              domNode.attribs.href,
                              //  @ts-ignore
                              domNode.children[0].data
                            );
                          }
                        }
                        return domNode;
                      }
                    })}
                  </>
                </Paragraph>
              </div>
            );
          }
          return RenderRichTextInlineComp(blok as RichTextInlineComp);
        })}
      </PromosLegalsContainer>
      <ButtonWrapper>
        <Button
          icon={expanded ? "chevronUp" : "chevronDown"}
          iconPosition="end"
          qaLabel="read-more-button"
          onClick={toggleExpanded}
          variant="tertiary"
          overrideColors={{
            default: TRANSPARENT_BUTTON_STYLE,
            hovered: TRANSPARENT_BUTTON_STYLE,
            active: TRANSPARENT_BUTTON_STYLE,
            iconActiveColor: TRANSPARENT_BUTTON_STYLE,
            iconHoverColor: TRANSPARENT_BUTTON_STYLE,
            pressed: TRANSPARENT_BUTTON_STYLE
          }}
        >
          {expanded ? "Hide" : "Read more"}
        </Button>
      </ButtonWrapper>
    </PromosLegalsWrapper>
  );
};

export default PromosLegalsContent;
